<script setup lang="ts">
import {ComponentPublicInstance, computed, nextTick, onMounted, ref} from "vue";
import {useRoute, useRouter} from "vue-router";
import {Cake, ChevronRight, Gift, ScanQrCode} from "@lucide/vue";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import {Button} from "@/core/components/ui/button";
import {useToastStore} from "@/stores/toast.store";
import {useSystemStore} from "@/stores/system.store";
import DeontologyBanner from "@/birthday-gift/components/DeontologyBanner.vue";
import GiftCard from "@/birthday-gift/components/GiftCard.vue";
import GiftCreateCard from "@/birthday-gift/components/GiftCreateCard.vue";
import GiftAutoSummaryCard from "@/birthday-gift/components/GiftAutoSummaryCard.vue";
import GiftFormCard from "@/birthday-gift/components/GiftFormCard.vue";
import GiftPhonePreview from "@/birthday-gift/components/phone-preview/GiftPhonePreview.vue";
import GiftScanDialog from "@/birthday-gift/components/scan/GiftScanDialog.vue";
import useBirthdayGifts from "@/birthday-gift/composables/useBirthdayGifts";
import useBirthdayGiftSettings from "@/birthday-gift/composables/useBirthdayGiftSettings";
import useTodayBirthdays from "@/birthday-gift/composables/useTodayBirthdays";
import {birthdayGiftApi} from "@/container";
import {giftConflictOf} from "@/birthday-gift/interfaces/GiftConflictError";
import {
    BirthdayGift,
    BirthdayGiftRequest,
    BirthdayGiftSettings,
    GiftSendHour,
    GiftValidityDays,
    GiftVideo
} from "@/birthday-gift/interfaces";

const route = useRoute();
const router = useRouter();
const toast = useToastStore();
const systemStore = useSystemStore();

const {data: gifts, isLoading, mutate: refreshGifts} = useBirthdayGifts();
const {data: settings, mutate: refreshSettings} = useBirthdayGiftSettings();
const {data: todayVouchers, mutate: refreshTodayVouchers} = useTodayBirthdays();

/**
 * The gift being edited, held by id so the form always reads the freshest
 * copy: a change made elsewhere (the card switch) is not overwritten by a
 * stale snapshot when the form is saved.
 */
const editing = ref<number | 'new' | null>(null);
const saving = ref(false);
const conflictMessage = ref<string | null>(null);
const togglingGiftId = ref<number | null>(null);
const draft = ref<BirthdayGiftRequest | null>(null);
const scanOpen = ref(false);

const sendHour = computed<GiftSendHour>(() => settings.value?.send_hour ?? 9);
const todayCount = computed(() => todayVouchers.value?.length ?? 0);
const editedGift = computed(() => typeof editing.value === 'number'
    ? (gifts.value ?? []).find((gift) => gift.id === editing.value) ?? null
    : null);
const pharmacyName = computed(() => systemStore.environment?.entity?.name ?? 'votre pharmacie');

/** The preview follows the form while editing, the first gift otherwise. */
const previewSource = computed(() => draft.value ?? gifts.value?.[0] ?? null);

const previewValidityDays = computed<GiftValidityDays>(() => previewSource.value?.validity_days ?? 15);

const previewVideo = computed<GiftVideo>(() => ({
    video_type: previewSource.value?.video_type ?? 'stock',
    video_id: previewSource.value?.video_id ?? null,
    video_url: previewSource.value?.video_url ?? null,
}));

const formCard = ref<ComponentPublicInstance | null>(null);

const openForm = async (gift: number | 'new') => {
    conflictMessage.value = null;
    editing.value = gift;

    // The form sits below the cards grid: bring it into view once rendered.
    await nextTick();
    formCard.value?.$el?.scrollIntoView({behavior: 'smooth', block: 'start'});
};

const closeForm = () => {
    conflictMessage.value = null;
    editing.value = null;
    draft.value = null;
};

/**
 * Refresh the today counter, then once more after a short delay: saving the
 * first gift of the day triggers a voucher preparation running in background
 * on the server, whose result is not visible immediately.
 */
const refreshTodayCounter = (): void => {
    void refreshTodayVouchers();
    window.setTimeout(() => void refreshTodayVouchers(), 5000);
};

/**
 * Report a rejected write, surfacing the profile conflict in the form
 * rather than as a generic error.
 */
const reportError = (error: unknown, fallback: string): void => {
    const conflict = giftConflictOf(error);

    if (conflict) {
        conflictMessage.value = conflict.message;

        return;
    }

    toast.showError({content: fallback});
};

const submit = async (payload: BirthdayGiftRequest, hour: GiftSendHour) => {
    saving.value = true;
    conflictMessage.value = null;

    try {
        const gift = editedGift.value;

        if (gift) {
            await birthdayGiftApi().update(gift.id, payload);
        } else {
            await birthdayGiftApi().create(payload);
        }

        // The send hour is pharmacy-wide: only write it when it actually moved.
        if (hour !== sendHour.value) {
            await saveSettings({send_hour: hour});
        }

        await refreshGifts();
        refreshTodayCounter();
        toast.showSuccess({content: gift ? 'Cadeau enregistré.' : 'Cadeau créé.'});
        closeForm();
    } catch (error) {
        reportError(error, 'Le cadeau n\'a pas pu être enregistré.');
    } finally {
        saving.value = false;
    }
};

/**
 * Toggle the automatic send of a gift. Pessimistic: the switch only moves
 * once the server accepted, so a rejected profile conflict leaves the card
 * in its previous state.
 */
const toggleAuto = async (gift: BirthdayGift, enabled: boolean) => {
    togglingGiftId.value = gift.id;

    try {
        await birthdayGiftApi().update(gift.id, {auto_send: enabled});
        await refreshGifts();
    } catch (error) {
        const conflict = giftConflictOf(error);

        toast.showError({
            content: conflict?.message ?? 'L\'envoi automatique n\'a pas pu être modifié.'
        });
    } finally {
        togglingGiftId.value = null;
    }
};

const saveSettings = async (updated: BirthdayGiftSettings) => {
    try {
        await birthdayGiftApi().updateSettings(updated);
        await refreshSettings();
    } catch {
        toast.showError({content: 'Les réglages n\'ont pas pu être enregistrés.'});
    }
};

const openStatistics = (gift: BirthdayGift) => {
    router.push({
        name: 'patient-administration.communication.birthday-gifts.statistics',
        params: {giftId: gift.id},
    });
};

const openToday = () => {
    router.push({name: 'patient-administration.communication.birthday-gifts.today'});
};

// Deep link: ?edit=<giftId> from the statistics screen.
onMounted(async () => {
    const giftId = Number(route.query.edit);

    if (!giftId) {
        return;
    }

    const list = gifts.value ?? await birthdayGiftApi().index();
    const gift = list.find((item) => item.id === giftId);

    if (gift) {
        openForm(gift.id);
    }
});
</script>

<template>
  <BaseContainer class="p-4">
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 class="mb-1 text-2xl font-medium">
          Mes cadeaux d'anniversaire
        </h1>
        <p class="max-w-[720px] text-sm leading-relaxed text-muted-foreground">
          Le message est toujours le même, et le cadeau reste une surprise pour le patient.
          Vous créez seulement les cadeaux : chacun a son propre QR code, à présenter au comptoir.
        </p>
      </div>
      <div class="flex items-center gap-3">
        <button
          type="button"
          class="inline-flex items-center gap-1.5 rounded-full bg-primary-lightest px-3 py-1.5 text-xs font-semibold text-primary"
          @click="openToday"
        >
          <Cake class="h-4 w-4" /><span class="pt-1">{{ todayCount }} anniversaires aujourd'hui</span>
          <ChevronRight class="h-4 w-4 opacity-70" />
        </button>
        <Button
          class="rounded-full bg-secondary text-primary-dark hover:bg-secondary-dark"
          @click="scanOpen = true"
        >
          <ScanQrCode class="h-4 w-4" />Scanner un QR code
        </Button>
      </div>
    </div>

    <div class="mt-4">
      <DeontologyBanner />
    </div>

    <BaseFlexSpinner v-if="isLoading" />
    <div
      v-else
      class="mt-4 flex flex-wrap items-start gap-6"
    >
      <div class="flex min-w-[460px] flex-1 flex-col gap-4">
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-3">
          <GiftCard
            v-for="gift in gifts ?? []"
            :key="gift.id"
            :gift="gift"
            :send-hour="sendHour"
            :toggling="togglingGiftId === gift.id"
            @edit="openForm(gift.id)"
            @statistics="openStatistics(gift)"
            @toggle-auto="toggleAuto(gift, $event)"
          />
          <GiftCreateCard @create="openForm('new')" />
        </div>

        <GiftFormCard
          v-if="editing"
          ref="formCard"
          :key="editedGift?.id ?? 'new'"
          class="scroll-mt-10"
          :gift="editedGift"
          :send-hour="sendHour"
          :conflict-message="conflictMessage"
          :saving="saving"
          @submit="submit"
          @cancel="closeForm"
          @change="draft = $event"
        />

        <GiftAutoSummaryCard
          :gifts="gifts ?? []"
          :send-hour="sendHour"
          @open-today="openToday"
        />

        <div class="flex items-start gap-3 rounded-lg bg-primary-lightest px-4 py-4">
          <Gift class="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div>
            <div class="mb-1 text-sm font-semibold">
              Le cadeau reste une surprise
            </div>
            <div class="text-sm leading-relaxed text-gray-dark">
              Le patient reçoit toujours le même message et la même vidéo : il découvre son cadeau
              au comptoir, quand l'équipe scanne son QR code.
            </div>
          </div>
        </div>
      </div>

      <GiftPhonePreview
        class="sticky top-4 shrink-0"
        :pharmacy-name="pharmacyName"
        :validity-days="previewValidityDays"
        :video="previewVideo"
      />
    </div>

    <GiftScanDialog
      v-model:open="scanOpen"
      @redeemed="refreshGifts"
    />
  </BaseContainer>
</template>
